import React, { useState } from 'react';
import {
  Card,
  Typography,
  Box,
  Avatar,
  AvatarGroup,
  Chip,
  IconButton,
  Stack,
  Snackbar,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ContentCopy,
  OpenInNew,
  LocationOn,
} from '@mui/icons-material';
import type { OwnerInformationCardProps } from '../../types/intelligence';
import { getInitialsFromName, getOccupancyBadgeConfig } from '../../utils/ownerUtils';
import { getIntelligenceColors } from '../../utils/intelligenceTheme';

/**
 * Enhanced owner information card displaying property owner details from public records.
 *
 * Features:
 * - Multiple owner support with AvatarGroup displaying owner initials
 * - Owner-occupied vs investment property status badge
 * - Mailing address display with interactive actions
 * - Copy address to clipboard functionality
 * - Open address in Google Maps (external link)
 * - Loading skeleton states for async data
 * - Error state handling with user-friendly messages
 * - Dark mode support via intelligence theme
 *
 * @component
 * @param {OwnerInformationCardProps} props - Component props
 * @param {PropertyOwner | null} props.owner - Property owner information from public records
 * @param {boolean | null} props.ownerOccupied - Whether property is owner-occupied (true) or investment property (false)
 * @param {boolean} [props.loading=false] - Loading state indicator for async data fetching
 *
 * @example
 * ```tsx
 * <OwnerInformationCard
 *   owner={{
 *     name: "John Doe, Jane Doe",
 *     mailingAddress: {
 *       address: "123 Main St",
 *       city: "Springfield",
 *       state: "IL",
 *       zip: "62701"
 *     }
 *   }}
 *   ownerOccupied={true}
 *   loading={false}
 * />
 * ```
 */
const OwnerInformationCard: React.FC<OwnerInformationCardProps> = ({
  owner,
  ownerOccupied,
  loading,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const intelColors = getIntelligenceColors(isDark);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const textPrimary = isDark ? intelColors?.text.primary : theme.palette.text.primary;
  const textMuted = isDark ? intelColors?.text.tertiary : theme.palette.text.secondary;

  const handleCopyAddress = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setSnackbarMessage('Address copied to clipboard');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy address:', err);
      setSnackbarMessage('Failed to copy address');
      setSnackbarOpen(true);
    }
  };

  /**
   * Opens address in Google Maps in a new tab.
   * Note: window.open() is intentionally used here (vs. navigate) because this opens
   * an external URL (Google Maps), not an in-app route. Security flags noopener,noreferrer
   * are set to prevent reverse tabnabbing attacks.
   */
  const handleOpenInMaps = (address: string) => {
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <Card
        sx={{
          backgroundColor: isDark ? intelColors?.background.paper : alpha(theme.palette.background.paper, 0.4),
          border: `1px solid ${isDark ? intelColors?.border.primary : theme.palette.divider}`,
          borderRadius: 3,
          p: 2.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: textPrimary, mb: 2.5 }}>
          Owner Information
        </Typography>

        {loading && (
          <Typography variant="body2" sx={{ color: textMuted }}>
            Loading owner data...
          </Typography>
        )}

        {!loading && !owner && (
          <Typography variant="body2" sx={{ color: textMuted }}>
            No owner information available
          </Typography>
        )}

        {!loading && owner && (
          <Stack spacing={2}>
            {/* Horizontal Owner + Badge Layout */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              {/* Owner info (left) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 200 }}>
                {owner.names && owner.names.length > 1 ? (
                  <>
                    <AvatarGroup
                      max={3}
                      sx={{
                        '& .MuiAvatar-root': {
                          width: 48,
                          height: 48,
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          border: `2px solid ${theme.palette.background.paper}`,
                        },
                      }}
                    >
                      {owner.names.map((name, index) => (
                        <Avatar
                          key={index}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                            color: theme.palette.primary.main,
                          }}
                          alt={name}
                        >
                          {getInitialsFromName(name)}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                    <Box>
                      <Typography variant="body1" sx={{ color: textPrimary, fontWeight: 600, mb: 0.5 }}>
                        {owner.names[0]}
                      </Typography>
                      <Typography variant="caption" sx={{ color: textMuted }}>
                        +{owner.names.length - 1} co-owner{owner.names.length > 2 ? 's' : ''}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        color: theme.palette.primary.main,
                        fontSize: '1rem',
                        fontWeight: 600,
                      }}
                      alt={owner.names?.[0] || 'Owner'}
                    >
                      {getInitialsFromName(owner.names?.[0] || '')}
                    </Avatar>
                    <Typography variant="body1" sx={{ color: textPrimary, fontWeight: 600 }}>
                      {owner.names?.[0]}
                    </Typography>
                  </>
                )}
              </Box>

              {/* Occupancy badge (right) */}
              {ownerOccupied !== null && (() => {
                const badgeConfig = getOccupancyBadgeConfig(ownerOccupied, isDark);
                return badgeConfig ? (
                  <Chip
                    label={badgeConfig.label}
                    size="medium"
                    sx={{
                      backgroundColor: badgeConfig.backgroundColor,
                      color: badgeConfig.color,
                      border: `1px solid ${badgeConfig.color}`,
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      height: 32,
                    }}
                  />
                ) : null;
              })()}
            </Box>

            {/* Mailing Address with Left Border Accent */}
            {owner.mailingAddress?.formattedAddress && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 2,
                  backgroundColor: isDark
                    ? alpha(intelColors?.background.primary || theme.palette.background.default, 0.5)
                    : alpha(theme.palette.background.default, 0.5),
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  borderRadius: 1,
                }}
              >
                <LocationOn
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: '1.25rem',
                    mt: 0.25,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '0.7rem',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    Mailing Address
                  </Typography>
                  <Typography variant="body2" sx={{ color: textPrimary }}>
                    {owner.mailingAddress.formattedAddress}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => owner.mailingAddress?.formattedAddress && handleCopyAddress(owner.mailingAddress.formattedAddress)}
                    sx={{
                      color: textMuted,
                      '&:hover': {
                        color: textPrimary,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                    title="Copy address"
                    aria-label="Copy address to clipboard"
                  >
                    <ContentCopy sx={{ fontSize: '1rem' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => owner.mailingAddress?.formattedAddress && handleOpenInMaps(owner.mailingAddress.formattedAddress)}
                    sx={{
                      color: textMuted,
                      '&:hover': {
                        color: textPrimary,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                    title="Open in Maps"
                    aria-label="Open address in Google Maps"
                  >
                    <OpenInNew sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Disclaimer */}
            <Typography
              variant="caption"
              sx={{
                color: textMuted,
                fontSize: '0.7rem',
                lineHeight: 1.4,
              }}
            >
              Owner information is sourced from public records and may not reflect recent changes. Verify during due diligence.
            </Typography>
          </Stack>
        )}
      </Card>

      {/* Snackbar for copy feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default OwnerInformationCard;
